import { CreateWithdrawalDto } from '@starsoft/common/dto/withdrawals/baseWithdrawalInputDto/create-withdrawal-input.dto';
import { useAxiosInstance, useWithdrawCallbacks } from '@starsoft/common/hooks';
import { Withdraw } from '@starsoft/common/models';
import { DetailedWithdrawal } from '@starsoft/common/models/withdraw/detailed';
import { useMutation } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { UseWithdrawalProps } from '../useWithdraw/props';
import { fetchWithdrawals } from './service';

/**
 * Custom hook to handle Binance Pay withdrawal creation
 *
 * This hook manages the mutation state for creating a Binance Pay withdrawal request,
 * using React Query's mutation capabilities. It handles the API call and success/error callbacks.
 * The mutation will retry up to 3 times on failure.
 *
 * @param {CreateWithdrawalDto} props - Optional configuration object
 * @param {Function} [props.onSuccess] - Custom success callback function
 *
 * @returns {Object} An object containing:
 *   - createWithdrawBinancePay: Function to trigger the withdrawal creation
 *     @param {WithdrawBinancePayInputDto} data - The withdrawal request data
 *   - isLoading: Boolean indicating if creation is in progress
 *   - isError: Boolean indicating if an error occurred
 */
export function useWithdrawals(props?: UseWithdrawalProps) {
  const axiosInstance: AxiosInstance = useAxiosInstance();
  const { onSuccess: customOnSucess } = props ?? {};
  const { onError, onSuccess } = useWithdrawCallbacks();

  /**
   * Success handler that runs both custom and default success callbacks
   * @param {Withdraw} withdraw - The created withdrawal data
   */
  function handleOnSuccess(withdraw: DetailedWithdrawal) {
    customOnSucess?.();
    onSuccess(withdraw);
  }

  const {
    mutate,
    isPending: isLoading,
    isError,
  } = useMutation({
    mutationFn: (data: CreateWithdrawalDto) =>
      fetchWithdrawals(data, axiosInstance),
    onSuccess: handleOnSuccess,
    onError,
  });

  return {
    createWithdrawals: mutate,
    isLoading,
    isError,
  };
}
